import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  isFutureDate,
  isNameTooShort,
  isValidDateOfBirth,
  MAX_IDENTITY_FIELD_LENGTH,
} from "../../utils/schoolFeeIdentity";

type Field = "matricule" | "firstName" | "lastName" | "dateOfBirth";

export type FieldMessage = {
  error: boolean;
  helperText?: string;
  onBlur: () => void;
};

const overMax = (value: string) =>
  value.trim().length > MAX_IDENTITY_FIELD_LENGTH;

/**
 * One message per student-details field, shared by the manual and the
 * registration forms. An empty field is reported only once the payer has left
 * it, so a fresh form does not open covered in errors.
 */
const useIdentityMessages = (values: Record<Field, string>) => {
  const { t } = useTranslation();
  const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({});

  const touch = (field: Field) => () =>
    setTouched(previous =>
      previous[field] ? previous : { ...previous, [field]: true }
    );

  const message = (field: Field, problem: string | null, hint?: string) => ({
    error: problem !== null,
    helperText: problem ?? hint,
    onBlur: touch(field),
  });

  const empty = (field: Field) =>
    touched[field] === true && values[field].trim() === "";

  const required = t("schoolfee.identity.required");

  const name = (field: "firstName" | "lastName", overMaxKey: string) =>
    message(
      field,
      empty(field)
        ? required
        : isNameTooShort(values[field])
        ? t("schoolfee.identity.name_too_short")
        : overMax(values[field])
        ? t(overMaxKey)
        : null
    );

  const dob = values.dateOfBirth;

  return {
    matricule: message(
      "matricule",
      empty("matricule")
        ? required
        : overMax(values.matricule)
        ? t("schoolfee.identity.matricule_above_max_len")
        : null,
      t("schoolfee.identity.matricule_hint")
    ),
    firstName: name("firstName", "schoolfee.student.firstname_above_max_len"),
    lastName: name("lastName", "schoolfee.student.lastname_above_max_len"),
    dateOfBirth: message(
      "dateOfBirth",
      empty("dateOfBirth")
        ? required
        : isFutureDate(dob)
        ? t("schoolfee.identity.date_of_birth_future")
        : dob !== "" && !isValidDateOfBirth(dob)
        ? t("schoolfee.identity.date_of_birth_invalid")
        : null,
      t("schoolfee.identity.date_of_birth_hint")
    ),
  } satisfies Record<Field, FieldMessage>;
};

export default useIdentityMessages;
